import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClub } from '@/contexts/ClubContext';
import { useI18n } from '@/contexts/I18nContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Users, UserPlus, Mail, Edit, Trash2, Search, Upload, FileSpreadsheet } from 'lucide-react';
import StarRating from '@/components/StarRating';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ApiClubMember, ApiQueueEntry, Member } from '@/types';
import { PaginationControls } from '@/components/ui/pagination-controls';
import './MemberManagement.css';
import PageSpinner from '@/components/PageSpinner';
import { PageHeaderSkeleton, TableSkeleton } from '@/components/PageSkeletons';
import { billingService, feedbackService, memberService, queueService, userService } from '@/services/storageService';
import { getLatestEvaluation, mapEvaluationsFromApi } from '@/lib/clubApiMappers';
import { buildAvatarDataUri } from '@/lib/avatarBuilder';

interface FormData {
  firstName: string;
  lastName: string;
  nickName: string;
  email: string;
  role: 'player' | 'coach' | 'admin';
  packageType?: 'package1' | 'package2' | 'package3';
}

type RoleOption = {
  value: FormData['role'];
  label: string;
  enabled: boolean;
  disabledReason?: string;
};

type BulkRole = 'player' | 'coach';

type BulkDraftMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  selected: boolean;
};

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  nickName: '',
  email: '',
  role: 'player',
  packageType: undefined,
};

export default function MemberManagement() {
  const { t } = useI18n();
  const { activeClubId, currentClubRole } = useClub();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const clubId = activeClubId ? Number(activeClubId) : null;
  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number) as Promise<ApiClubMember[]>,
    enabled: Boolean(clubId),
  });

  const queueQuery = useQuery({
    queryKey: ['club-queue', clubId],
    queryFn: () => queueService.list(clubId as number) as Promise<ApiQueueEntry[]>,
    enabled: Boolean(clubId),
  });
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedBillingPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const isEvaluationsFeatureAvailable = resolvedBillingPackageCode !== 'package1';

  const evalsQuery = useQuery({
    queryKey: ['club-evals', clubId],
    queryFn: () => feedbackService.listForClub(clubId as number),
    enabled: Boolean(clubId) && isEvaluationsFeatureAvailable,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCapacityDialogOpen, setIsCapacityDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedActiveIds, setSelectedActiveIds] = useState<string[]>([]);
  const [isApplyingCapacity, setIsApplyingCapacity] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'player' | 'coach'>('all');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isAdding, setIsAdding] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkRole, setBulkRole] = useState<BulkRole>('player');
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkRows, setBulkRows] = useState<BulkDraftMember[]>([]);
  const [bulkInlineError, setBulkInlineError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [addDots, setAddDots] = useState('.');
  const [saveDots, setSaveDots] = useState('');
  const bulkCsvInputId = 'bulk-members-csv-input';

  useEffect(() => {
    if (!isAdding) {
      setAddDots('.');
      return;
    }
    const frames = ['.', '..', '...'];
    let idx = 0;
    setAddDots(frames[idx]);
    const timer = window.setInterval(() => {
      setAddDots(frames[idx % frames.length]);
      idx += 1;
    }, 400);
    return () => window.clearInterval(timer);
  }, [isAdding]);

  useEffect(() => {
    if (!isSaving) {
      setSaveDots('');
      return;
    }
    const frames = ['.', '..', '...'];
    let idx = 0;
    const timer = window.setInterval(() => {
      setSaveDots(frames[idx % frames.length]);
      idx += 1;
    }, 350);
    return () => window.clearInterval(timer);
  }, [isSaving]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
  }, []);

  const handleInputChange = useCallback((field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value as any }));
  }, []);

  const parseBulkCsv = useCallback((content: string) => {
    const lines = content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (lines.length === 0) {
      throw new Error('CSV file is empty.');
    }

    const header = lines[0]
      .split(',')
      .map((col) => col.trim().toLowerCase().replace(/\s+/g, '_'));

    const firstNameIdx = header.findIndex((h) => h === 'firstname' || h === 'first_name' || h === 'first-name');
    const lastNameIdx = header.findIndex((h) => h === 'lastname' || h === 'last_name' || h === 'last-name');
    const emailIdx = header.findIndex((h) => h === 'email' || h === 'email_address' || h === 'email-address');

    if (firstNameIdx < 0 || lastNameIdx < 0 || emailIdx < 0) {
      throw new Error('CSV must include headers: firstname, lastname, email.');
    }

    const rows: Array<{ firstName: string; lastName: string; email: string }> = [];
    for (let i = 1; i < lines.length; i += 1) {
      const cols = lines[i].split(',').map((v) => v.trim().replace(/^"(.*)"$/, '$1'));
      const firstName = cols[firstNameIdx] || '';
      const lastName = cols[lastNameIdx] || '';
      const email = cols[emailIdx] || '';
      if (!firstName && !lastName && !email) continue;
      rows.push({ firstName, lastName, email });
    }

    if (rows.length === 0) {
      throw new Error('No member rows found in CSV.');
    }
    return rows;
  }, []);

  const handleBulkCsvSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!clubId) {
      toast.error('Select a club first.');
      return;
    }
    if (bulkRole === 'coach' && resolvedBillingPackageCode === 'package1') {
      toast.error('Selected role is not available in this package.');
      return;
    }

    try {
      setIsBulkUploading(true);
      const text = await file.text();
      const rows = parseBulkCsv(text);
      setBulkInlineError('');
      setBulkFileName(file.name);
      setBulkRows(
        rows.map((row, idx) => ({
          id: `${idx + 1}`,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          selected: true,
        })),
      );
    } catch (err: any) {
      toast.error(err?.message || 'Failed to import CSV file');
    } finally {
      setIsBulkUploading(false);
    }
  }, [bulkRole, clubId, parseBulkCsv, resolvedBillingPackageCode]);

  const handleAddMember = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.firstName || !formData.lastName || !formData.email) {
        toast.error('Please fill in required fields');
        return;
      }
      if (!clubId) {
        toast.error('Select a club first');
        return;
      }

      try {
        setIsAdding(true);
        if (!isRoleAllowed(formData.role)) {
          toast.error('Selected role is not available in this package.');
          return;
        }
        const res = await userService.adminAddMember({
          clubId,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          nickname: formData.nickName,
          role: formData.role,
        }) as any;
        await queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
        setIsAddDialogOpen(false);
        resetForm();
        toast.success('Member added successfully!');
      } catch (err: any) {
        toast.error(err?.message || 'Failed to add member');
        console.error(err);
      } finally {
        setIsAdding(false);
      }
    },
    [formData, clubId, queryClient, resetForm],
  );

  const handleEditMember = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedMember) return;

      try {
        setIsSaving(true);
        if (!clubId) throw new Error('Select a club first');
        if (!isRoleAllowed(formData.role)) {
          toast.error('Selected role is not available in this package.');
          return;
        }
        await memberService.updateDetails(clubId, selectedMember.id, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          nickname: formData.nickName,
          email: formData.email,
        });
        const nextRole =
          formData.role === 'admin' ? 'ADMIN' : formData.role === 'coach' ? 'COACH' : 'PLAYER';
        await memberService.updateRole(clubId, selectedMember.id, nextRole);
        await queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });

        toast.success('Member updated!');
        setIsEditDialogOpen(false);
        setSelectedMember(null);
      } catch (err: any) {
        toast.error(err?.message || 'Failed to update member');
        console.error(err);
      } finally {
        setIsSaving(false);
      }
    },
    [selectedMember, formData, clubId, queryClient],
  );

  const handleDeleteMember = useCallback(
    async (member: Member) => {
      if (
        confirm(
          t('member_remove_confirm', 'Are you sure you want to remove {name}?').replace(
            '{name}',
            `${member.firstName} ${member.lastName}`,
          ),
        )
      ) {
        try {
          if (!clubId) throw new Error('Select a club first');
          await memberService.remove(clubId, member.id);
          await queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
          toast.success('Member removed');
        } catch (err: any) {
          toast.error(err?.message || 'Cannot delete member while they are queued or playing');
        }
      }
    },
    [clubId, queryClient],
  );

  const openEditDialog = useCallback(
    (member: Member) => {
      setSelectedMember(member);
      setFormData({
        firstName: member.firstName,
        lastName: member.lastName,
        nickName: member.nickName || '',
        email: member.email,
        role: member.role === 'admin' ? 'admin' : member.role === 'member' ? 'player' : (member.role as any),
        packageType: member.packageType || undefined,
      });
      setIsEditDialogOpen(true);
    },
    [user],
  );

  // Show all members (including admin/current user) + search
  const evalsByMember = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (evalsQuery.data || []).forEach((ev: any) => {
      const id = String(ev.player_id ?? ev.playerId ?? '');
      if (!id) return;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(ev);
    });
    return grouped;
  }, [evalsQuery.data]);

  const members = useMemo(() => {
    const queue = queueQuery.data || [];
    const queuedIds = new Set(queue.filter((q) => q.status === 'QUEUED').map((entry) => String(entry.member_id)));
    const playingIds = new Set(queue.filter((q) => q.status === 'PLAYING').map((entry) => String(entry.member_id)));
    return (membersQuery.data || []).map((m) => ({
      evaluations: isEvaluationsFeatureAvailable
        ? mapEvaluationsFromApi(evalsByMember[String(m.member_id)] || [])
        : [],
      id: String(m.member_id),
      userId: String(m.member_id),
      firstName: m.first_name,
      lastName: m.last_name,
      email: m.email,
      nickName: m.nickname || undefined,
      role: m.role.toLowerCase() as Member['role'],
      skillLevel: undefined,
      avatarUrl: buildAvatarDataUri(m.collection_name, m.avatar_seed),
      avatarSeed: m.avatar_seed || undefined,
      avatarCollection: m.collection_name || undefined,
      avatarName: m.avatar_name || undefined,
      hasEvaluation: isEvaluationsFeatureAvailable
        ? m.role.toLowerCase() === 'coach' || (evalsByMember[String(m.member_id)] || []).length > 0
        : false,
      equipment: [],
      isAvailable: queuedIds.has(String(m.member_id)),
      isPlaying: playingIds.has(String(m.member_id)),
      isActive: m.is_active !== false,
    }));
  }, [membersQuery.data, queueQuery.data, evalsByMember, isEvaluationsFeatureAvailable]);

  const normalizeRole = (role?: string | null) => String(role ?? '').trim().toLowerCase();
  const memberRoleForUser = useMemo(() => {
    if (!user) return null;
    const list = (membersQuery.data || []) as ApiClubMember[];
    const match = list.find(
      (m) =>
        String(m.member_id) === String(user.id) ||
        (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
    );
    return match?.role ? String(match.role).toLowerCase() : null;
  }, [membersQuery.data, user]);
  const roleForAccess = normalizeRole(memberRoleForUser || currentClubRole || user?.role || null);

  const filteredMembers = useMemo(() => {
    const q = searchQuery.toLowerCase();

    return members
      .filter((m) => {
        if (roleFilter === 'all') return true;
        if (roleFilter === 'coach') return m.role === 'coach';
        return m.role === 'player' || m.role === 'member';
      })
      .filter((m) =>
        `${m.firstName} ${m.lastName} ${m.email} ${m.nickName || ''}`
          .toLowerCase()
          .includes(q),
      )
      // ✅ SORT BY FULL NAME (ASCENDING)
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [members, roleFilter, searchQuery]);


  const total = filteredMembers.length;
  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  const displayedMembers = filteredMembers.slice(startIdx, endIdx);

  const isAdmin = roleForAccess === 'admin';
  const capacityQuery = useQuery({
    queryKey: ['club-member-capacity', clubId],
    queryFn: () => memberService.getCapacity(clubId as number),
    enabled: Boolean(clubId && isAdmin),
  });
  const memberLimit = capacityQuery.data?.max_active_members ?? 0;
  const activeMemberCount = capacityQuery.data?.active_members ?? 0;
  const isOverLimit = Boolean(capacityQuery.data?.is_over_limit);
  const resolvedPackageCode = String(
    capacityQuery.data?.package_code || 'package1',
  )
    .trim()
    .toLowerCase();
  const roleOptions = useMemo<RoleOption[]>(() => {
    const coachDisabledReason = 'Coach role is available on Pro (Package 2) and Elite (Package 3).';
    const adminDisabledReason = 'Admin role is available on Elite (Package 3) only.';
    if (resolvedPackageCode === 'package3') {
      return [
        { value: 'player', label: 'Player', enabled: true },
        { value: 'coach', label: 'Coach', enabled: true },
        { value: 'admin', label: 'Admin', enabled: true },
      ];
    }
    if (resolvedPackageCode === 'package2') {
      return [
        { value: 'player', label: 'Player', enabled: true },
        { value: 'coach', label: 'Coach', enabled: true },
        { value: 'admin', label: 'Admin', enabled: false, disabledReason: adminDisabledReason },
      ];
    }
    return [
      { value: 'player', label: 'Player', enabled: true },
      { value: 'coach', label: 'Coach', enabled: false, disabledReason: coachDisabledReason },
      { value: 'admin', label: 'Admin', enabled: false, disabledReason: adminDisabledReason },
    ];
  }, [resolvedPackageCode]);
  const isRoleAllowed = useCallback(
    (role: FormData['role']) => roleOptions.some((opt) => opt.value === role && opt.enabled),
    [roleOptions],
  );
  const remainingBulkSlots = Math.max(0, memberLimit - activeMemberCount);
  const selectedBulkCount = bulkRows.filter((row) => row.selected).length;
  const bulkRoleOptions = useMemo(
    () => [
      { value: 'player' as const, label: 'Player', enabled: true },
      {
        value: 'coach' as const,
        label: 'Coach',
        enabled: resolvedPackageCode !== 'package1',
      },
    ],
    [resolvedPackageCode],
  );

  const resetBulkState = useCallback(() => {
    setBulkFileName('');
    setBulkRows([]);
    setIsBulkUploading(false);
    setBulkInlineError('');
    setBulkRole('player');
  }, []);

  const syncSelectionToLimit = useCallback(
    (rows: BulkDraftMember[]) => {
      if (remainingBulkSlots <= 0) {
        return rows.map((r) => ({ ...r, selected: false }));
      }
      let remaining = remainingBulkSlots;
      return rows.map((row) => {
        if (!row.selected) return row;
        if (remaining > 0) {
          remaining -= 1;
          return row;
        }
        return { ...row, selected: false };
      });
    },
    [remainingBulkSlots],
  );

  const toggleBulkRow = useCallback(
    (id: string) => {
      setBulkRows((prev) => {
        const current = prev.find((r) => r.id === id);
        if (!current) return prev;
        setBulkInlineError('');
        if (current.selected) {
          return prev.map((row) => (row.id === id ? { ...row, selected: false } : row));
        }
        const selected = prev.filter((r) => r.selected).length;
        if (selected >= remainingBulkSlots) {
          toast.error(`You can only select ${remainingBulkSlots} members based on remaining plan slots.`);
          return prev;
        }
        return prev.map((row) => (row.id === id ? { ...row, selected: true } : row));
      });
    },
    [remainingBulkSlots],
  );

  const handleBulkImportSubmit = useCallback(async () => {
    if (!clubId) {
      toast.error('Select a club first.');
      return;
    }
    if (bulkRole === 'coach' && resolvedPackageCode === 'package1') {
      toast.error('Coach role is not available in this package.');
      return;
    }
    const selectedRows = bulkRows.filter((row) => row.selected);
    if (selectedRows.length === 0) {
      toast.error('Select at least one member to import.');
      return;
    }
    const invalidRow = selectedRows.find(
      (row) => !row.firstName.trim() || !row.lastName.trim() || !row.email.trim(),
    );
    if (invalidRow) {
      toast.error('Selected rows must include first name, last name, and email.');
      return;
    }
    try {
      setBulkInlineError('');
      setIsBulkUploading(true);
      const result = await userService.adminAddMembersBulk({
        clubId,
        role: bulkRole,
        rows: selectedRows.map((row) => ({
          firstName: row.firstName.trim(),
          lastName: row.lastName.trim(),
          email: row.email.trim(),
        })),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['club-members', clubId] }),
        queryClient.invalidateQueries({ queryKey: ['club-member-capacity', clubId] }),
      ]);
      if (result.not_added_count > 0) {
        toast.success(
          `Imported ${result.added_count}/${result.requested_count}. ${result.not_added_count} were not added.`,
          { duration: 7000 },
        );
      } else {
        toast.success(`Imported ${result.added_count} ${bulkRole === 'coach' ? 'coaches' : 'players'} successfully.`);
      }
      setIsBulkDialogOpen(false);
      resetBulkState();
    } catch (err: any) {
      const message = err?.message || 'Failed to import selected members';
      setBulkInlineError(message);
    } finally {
      setIsBulkUploading(false);
    }
  }, [bulkRole, bulkRows, clubId, queryClient, resetBulkState, resolvedPackageCode]);

  useEffect(() => {
    if (!isRoleAllowed(formData.role)) {
      setFormData((prev) => ({ ...prev, role: 'player' }));
    }
  }, [formData.role, isRoleAllowed]);

  useEffect(() => {
    if (bulkRole === 'coach' && resolvedPackageCode === 'package1') {
      setBulkRole('player');
    }
  }, [bulkRole, resolvedPackageCode]);

  useEffect(() => {
    if (bulkRows.length === 0) return;
    setBulkRows((prev) => syncSelectionToLimit(prev));
  }, [remainingBulkSlots, bulkRows.length, syncSelectionToLimit]);

  useEffect(() => {
    if (!isOverLimit || !memberLimit || members.length === 0) {
      if (!isOverLimit) {
        setIsCapacityDialogOpen(false);
      }
      return;
    }
    const sorted = [...members].sort((a, b) => {
      const roleScoreA = a.role === 'admin' ? 0 : 1;
      const roleScoreB = b.role === 'admin' ? 0 : 1;
      if (roleScoreA !== roleScoreB) return roleScoreA - roleScoreB;
      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });
    const activeIds = sorted.filter((m) => m.isActive !== false).map((m) => m.id);
    if (selectedActiveIds.length === 0) {
      setSelectedActiveIds(activeIds.slice(0, memberLimit));
    }
    setIsCapacityDialogOpen(true);
  }, [isOverLimit, memberLimit, members, selectedActiveIds.length]);

  const toggleCapacitySelection = useCallback((memberId: string) => {
    setSelectedActiveIds((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      }
      if (prev.length >= memberLimit) {
        toast.error(`You can select only ${memberLimit} members for this package.`);
        return prev;
      }
      return [...prev, memberId];
    });
  }, [memberLimit]);

  const applyCapacitySelection = useCallback(async () => {
    if (!clubId) return;
    if (selectedActiveIds.length === 0) {
      toast.error('Select at least one member to keep active.');
      return;
    }
    if (selectedActiveIds.length > memberLimit) {
      toast.error(`You can select at most ${memberLimit} members.`);
      return;
    }
    try {
      setIsApplyingCapacity(true);
      await memberService.applyCapacity(
        clubId,
        selectedActiveIds.map((id) => Number(id)),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['club-members', clubId] }),
        queryClient.invalidateQueries({ queryKey: ['club-member-capacity', clubId] }),
        queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] }),
      ]);
      toast.success(t('members_capacity_updated', 'Active members updated to match your package limit.'));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to apply member limit selection');
    } finally {
      setIsApplyingCapacity(false);
    }
  }, [clubId, memberLimit, queryClient, selectedActiveIds]);

  if (!clubId) {
    return <PageSpinner label="Select a club to manage members." />;
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Only admins can manage members.</p>
      </div>
    );
  }

  if (membersQuery.isLoading || queueQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <TableSkeleton rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Users className="text-primary shrink-0" />
            <span className="truncate">Members</span>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Manage your club members ({members.length} total)
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Dialog
            open={isBulkDialogOpen}
            onOpenChange={(open) => {
              if (isBulkUploading) return;
              setIsBulkDialogOpen(open);
              if (!open) resetBulkState();
            }}
          >
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto" disabled={isOverLimit}>
                <Upload size={16} className="mr-2" />
                Import Members CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[85vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle>Import Members CSV</DialogTitle>
                <DialogDescription>
                  Upload CSV with headers: <strong>firstname</strong>, <strong>lastname</strong>, <strong>email</strong>.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[220px_1fr]">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={bulkRole}
                    onValueChange={(v) => {
                      setBulkRole(v as BulkRole);
                      setBulkInlineError('');
                    }}
                    disabled={isBulkUploading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {bulkRoleOptions.map((opt) =>
                        opt.enabled ? (
                          <SelectItem key={`bulk-role-${opt.value}`} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ) : (
                          <div
                            key={`bulk-role-disabled-${opt.value}`}
                            tabIndex={0}
                            className="flex w-full cursor-not-allowed select-none items-center rounded-sm px-8 py-1.5 text-sm text-muted-foreground/70 outline-none"
                          >
                            {opt.label}
                            <span className="ml-auto text-[11px] uppercase tracking-wide">Unavailable</span>
                          </div>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={bulkCsvInputId}>CSV File</Label>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      id={bulkCsvInputId}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={handleBulkCsvSelect}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        const el = document.getElementById(bulkCsvInputId) as HTMLInputElement | null;
                        el?.click();
                      }}
                      disabled={isBulkUploading || (bulkRole === 'coach' && resolvedPackageCode === 'package1')}
                    >
                      <FileSpreadsheet size={16} className="mr-2" />
                      Choose CSV
                    </Button>
                    <span className="text-sm text-muted-foreground truncate">
                      {bulkFileName || 'No file selected'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 text-sm">
                <div className="rounded-md border bg-muted/20 px-3 py-2">
                  Remaining member slots: <strong>{remainingBulkSlots}</strong>
                </div>
                <div className="rounded-md border bg-muted/20 px-3 py-2">
                  Selected: <strong>{selectedBulkCount}</strong>
                </div>
                <div className="rounded-md border bg-muted/20 px-3 py-2">
                  CSV rows: <strong>{bulkRows.length}</strong>
                </div>
              </div>

              {bulkInlineError ? (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {bulkInlineError}
                </div>
              ) : null}

              <div className="rounded-md border">
                <div className="grid grid-cols-[44px_1fr_1fr] gap-2 border-b bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide">
                  <span>Select</span>
                  <span>Name</span>
                  <span>Email</span>
                </div>
                <div className="max-h-[42vh] overflow-y-auto">
                  {bulkRows.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                      Upload a CSV to preview members.
                    </div>
                  ) : (
                    bulkRows.map((row) => {
                      const disableUnchecked = !row.selected && selectedBulkCount >= remainingBulkSlots;
                      const isRowMuted = remainingBulkSlots === 0 || disableUnchecked;
                      return (
                        <div
                          key={row.id}
                          className={`grid grid-cols-[44px_1fr_1fr] gap-2 border-b px-3 py-2 items-center last:border-b-0 ${
                            isRowMuted ? 'opacity-50' : ''
                          }`}
                        >
                          <Checkbox
                            checked={row.selected}
                            disabled={remainingBulkSlots === 0 || disableUnchecked || isBulkUploading}
                            onCheckedChange={() => toggleBulkRow(row.id)}
                          />
                          <div className={`text-sm truncate ${isRowMuted ? 'text-muted-foreground' : ''}`}>
                            {row.firstName} {row.lastName}
                          </div>
                          <div className={`text-sm truncate ${isRowMuted ? 'text-muted-foreground' : ''}`}>
                            {row.email}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={resetBulkState} disabled={isBulkUploading}>
                  Reset
                </Button>
                <Button
                  type="button"
                  onClick={handleBulkImportSubmit}
                  disabled={isBulkUploading || selectedBulkCount === 0 || remainingBulkSlots === 0}
                >
                  {isBulkUploading ? 'Importing...' : `Import Selected (${selectedBulkCount})`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Member */}
          <Dialog
            open={isAddDialogOpen}
            onOpenChange={(open) => {
              setIsAddDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            {isAdmin && (
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto" disabled={isOverLimit}>
                  <UserPlus size={18} className="mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
            )}

            <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Member</DialogTitle>
                <DialogDescription>
                  Add a new member to your club. They will receive a first-time login link.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleAddMember} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="add-firstName">First Name *</Label>
                    <Input
                      id="add-firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="add-lastName">Last Name *</Label>
                    <Input
                      id="add-lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="add-nickName">Nickname</Label>
                  <Input
                    id="add-nickName"
                    value={formData.nickName}
                    onChange={(e) => handleInputChange('nickName', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="add-email">Email *</Label>
                  <Input
                    id="add-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v as any)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((opt) =>
                        opt.enabled ? (
                          <SelectItem key={`add-role-${opt.value}`} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ) : (
                          <div
                            key={`add-role-disabled-${opt.value}`}
                            tabIndex={0}
                            className="flex w-full cursor-not-allowed select-none items-center rounded-sm px-8 py-1.5 text-sm text-muted-foreground/70 outline-none"
                          >
                            {opt.label}
                            <span className="ml-auto text-[11px] uppercase tracking-wide">Unavailable</span>
                          </div>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full" disabled={isAdding}>
                  {isAdding ? `Adding${addDots}` : 'Add Member'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      {isOverLimit && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm sm:text-base">
            {t('members_capacity_exceed_intro', 'Active members exceed your package limit. Keep up to')} <strong>{memberLimit}</strong> {t('members_capacity_active_members_for', 'active members for')}{' '}
            <strong>{String(capacityQuery.data?.package_code || '').toUpperCase()}</strong>.
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={roleFilter}
          onValueChange={(value) => {
            setRoleFilter(value as 'all' | 'player' | 'coach');
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Filter role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="player">Players</SelectItem>
            <SelectItem value="coach">Coaches</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Count Row */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {total === 0 ? 'No records' : `Showing ${startIdx + 1} - ${endIdx} of ${total}`}
        </p>
        {total > pageSize && (
          <div className="text-sm text-muted-foreground">
            Page {currentPage} of {Math.ceil(total / pageSize)}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {displayedMembers.map((member) => {
          const isSelf = member.email.toLowerCase() === (user?.email || '').toLowerCase();
          const latest = getLatestEvaluation(member.evaluations);
          const ratingValue = !isEvaluationsFeatureAvailable
            ? 0
            : member.role === 'coach'
              ? latest?.rating ?? 5
              : latest?.rating ?? 0;

          return (
            <Card key={member.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                {/* Top */}
                <div className="flex items-start justify-between gap-3 member-card-top">
                  <div className="flex-1 min-w-0 member-card-info">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10 shrink-0">
                        {member.avatarUrl && (
                          <AvatarImage src={member.avatarUrl} alt={member.avatarName || `${member.firstName} ${member.lastName}`} />
                        )}
                        <AvatarFallback>
                          {(member.firstName?.[0] || '') + (member.lastName?.[0] || '')}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate max-w-[220px]">
                            {member.firstName} {member.lastName}
                          </h3>

                          {member.role === 'coach' && (
                            <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                              Coach
                            </span>
                          )}

                          {member.role === 'admin' && (
                            <span className="text-xs bg-muted text-foreground px-2 py-0.5 rounded-full">
                              Admin
                            </span>
                          )}

                          {isSelf && (
                            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                              You
                            </span>
                          )}
                        </div>

                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Rating</span>
                          <StarRating value={ratingValue} size={14} />
                        </div>

                        {member.nickName && (
                          <p className="text-sm text-muted-foreground mt-1 truncate">"{member.nickName}"</p>
                        )}

                        <div className="text-xs text-muted-foreground mt-1 capitalize">{member.role}</div>

                        {member.isActive === false && (
                          <div className="text-xs text-destructive mt-1">Inactive</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 shrink-0 member-card-actions">
                    {isAdmin && !isSelf && member.role !== 'admin' && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-sidebar-primary hover:text-white"
                          onClick={() => openEditDialog(member)}
                          aria-label="Edit"
                        >
                          <Edit size={16} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-w-[72px] hover:bg-sidebar-primary hover:text-white"
                          aria-label="Toggle active"
                          onClick={async () => {
                            const next = !(member.isActive !== false ? true : false);
                            try {
                              if (!clubId) throw new Error('Select a club first');
                              await memberService.updateStatus(clubId, member.id, next);
                              await queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
                              toast.success(`Member ${next ? 'activated' : 'deactivated'}`);
                            } catch (err: any) {
                              const msg = err?.message || 'Cannot change status while member is queued or playing';
                              toast.error(msg);
                            }
                          }}
                        >
                          {member.isActive === false ? (
                            <span className="text-xs font-semibold">Active</span>
                          ) : (
                            <span className="text-xs font-semibold">Inactive</span>
                          )}
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-sidebar-primary hover:text-white"
                          onClick={() => handleDeleteMember(member)}
                          aria-label="Delete"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Contact */}
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                    <Mail size={14} className="shrink-0" />
                    <span className="truncate">{member.email}</span>
                  </div>
                </div>

                {/* Status chips */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {member.isPlaying && member.isActive !== false && (
                    <span className="text-xs bg-success/10 text-success px-2 py-1 rounded-full">Playing</span>
                  )}
                  {member.isAvailable && !member.isPlaying && member.isActive !== false && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">In Queue</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty state */}
      {total === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {searchQuery ? 'No members found matching your search.' : 'No members yet. Add your first member!'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <PaginationControls
          page={currentPage}
          pageSize={pageSize}
          total={total}
          onPageChange={setCurrentPage}
          className="mt-4"
        />
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
            <DialogDescription>Update member information.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditMember} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">First Name *</Label>
                <Input
                  id="edit-firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-lastName">Last Name *</Label>
                <Input
                  id="edit-lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-nickName">Nickname</Label>
              <Input
                id="edit-nickName"
                value={formData.nickName}
                onChange={(e) => handleInputChange('nickName', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((opt) =>
                    opt.enabled ? (
                      <SelectItem key={`edit-role-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ) : (
                      <div
                        key={`edit-role-disabled-${opt.value}`}
                        tabIndex={0}
                        className="flex w-full cursor-not-allowed select-none items-center rounded-sm px-8 py-1.5 text-sm text-muted-foreground/70 outline-none"
                      >
                        {opt.label}
                        <span className="ml-auto text-[11px] uppercase tracking-wide">Unavailable</span>
                      </div>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? `Saving${saveDots}` : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCapacityDialogOpen}
        onOpenChange={(open) => {
          if (isOverLimit && !open) return;
          setIsCapacityDialogOpen(open);
        }}
      >
        <DialogContent
          className="w-[95vw] max-w-2xl max-h-[85vh] overflow-hidden"
          onPointerDownOutside={(e) => {
            if (isOverLimit) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isOverLimit) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('members_select_active_title', 'Select Active Members')}</DialogTitle>
            <DialogDescription>
              {t('members_select_active_desc', 'Your current package allows up to {{limit}} active members. Select who should stay active.').replace('{{limit}}', String(memberLimit))}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/20 p-3 text-sm">
            {t('members_capacity_selected', 'Selected')}: <strong>{selectedActiveIds.length}</strong> / {memberLimit} | {t('members_capacity_current_active', 'Current active')}: {activeMemberCount}
          </div>
          <div className="max-h-[48vh] overflow-y-auto rounded-md border">
            <div className="divide-y">
              {[...members]
                .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                .map((member) => {
                  const isChecked = selectedActiveIds.includes(member.id);
                  const isDisabled = !isChecked && selectedActiveIds.length >= memberLimit;
                  return (
                    <label key={`cap-${member.id}`} className="flex items-center gap-3 p-3">
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled || isApplyingCapacity}
                        onCheckedChange={() => toggleCapacitySelection(member.id)}
                      />
                      <Avatar className="h-9 w-9 shrink-0">
                        {member.avatarUrl && (
                          <AvatarImage src={member.avatarUrl} alt={`${member.firstName} ${member.lastName}`} />
                        )}
                        <AvatarFallback>
                          {(member.firstName?.[0] || '') + (member.lastName?.[0] || '')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">
                          {member.firstName} {member.lastName}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">{member.email}</div>
                      </div>
                    </label>
                  );
                })}
            </div>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {!isOverLimit && (
              <Button type="button" variant="outline" onClick={() => setIsCapacityDialogOpen(false)} disabled={isApplyingCapacity}>
                {t('common_close', 'Close')}
              </Button>
            )}
            <Button type="button" onClick={applyCapacitySelection} disabled={isApplyingCapacity}>
              {isApplyingCapacity ? t('members_applying', 'Applying...') : t('members_apply_selection', 'Apply Selection')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
